import { useState } from 'react'
import { Briefcase, Edit, Trash2, Save, Plus, Eye, EyeOff } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import { Checkbox, Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'
import {
  getProfessionName,
  getDepartmentName,
  getDepartmentCode,
  getPositionName,
  getSimilarTermName
} from '../../utils/professionUtils'

const ProfessionsSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [isEditingProfessions, setIsEditingProfessions] = useState(false)
  const [editingProfessions, setEditingProfessions] = useState([])
  const [professionSaving, setProfessionSaving] = useState(false)
  const [deptOptions, setDeptOptions] = useState([])
  const [professionOptions, setProfessionOptions] = useState([])
  const [positionOptions, setPositionOptions] = useState([])
  const [similarTermOptions, setSimilarTermOptions] = useState([])
  const [professionOptionsLoaded, setProfessionOptionsLoaded] = useState(false)
  const [professionOptionsLoading, setProfessionOptionsLoading] = useState(false)

  const loadProfessionOptions = async () => {
    if (professionOptionsLoaded || professionOptionsLoading) return
    setProfessionOptionsLoading(true)
    try {
      const [departments, profs, positions, similar] = await Promise.all([
        libraries.getDepartments(),
        libraries.getProfessions(),
        libraries.getPositions(),
        libraries.getAllSimilarTerms()
      ])
      const norm = (r) => (Array.isArray(r?.data) ? r.data : r?.data?.data || [])
      setDeptOptions(norm(departments))
      setProfessionOptions(norm(profs))
      setPositionOptions(norm(positions))
      setSimilarTermOptions(norm(similar))
      setProfessionOptionsLoaded(true)
    } catch (err) {
      console.error('Error loading profession options:', err)
    } finally {
      setProfessionOptionsLoading(false)
    }
  }

  const emptyProfession = () => ({
    profession_id: null, profession_name: '',
    department_id: null, department_name: '', department_code: null,
    position_id: null, position_name: '',
    similar_term_id: null, similar_term_name: '', is_similar: false,
    is_primary: !(talent.professions?.some(p => p.is_primary)),
    public_visible: true
  })

  const openProfessionsEdit = async () => {
    // Предзаполняем форму агрегированными названиями — так сохранение заодно
    // приводит денормализованные поля в БД в соответствие со справочником
    setEditingProfessions((talent.professions || []).map(p => ({
      id: p.id,
      profession_id: p.profession_id || null,
      profession_name: getProfessionName(p),
      department_id: p.department_id || null,
      department_name: getDepartmentName(p),
      department_code: getDepartmentCode(p),
      position_id: p.position_id || null,
      position_name: getPositionName(p),
      similar_term_id: p.similar_term_id || null,
      similar_term_name: getSimilarTermName(p),
      is_similar: !!p.similar_term_id,
      is_primary: p.is_primary,
      public_visible: p.public_visible !== false
    })))
    setIsEditingProfessions(true)
    loadProfessionOptions()
  }

  const handleProfessionsSave = async () => {
    setProfessionSaving(true)
    try {
      const payload = editingProfessions.map(p => ({
        ...(p.id ? { id: p.id } : {}),
        profession_id: p.profession_id,
        profession_name: p.profession_name || null,
        department_id: p.department_id || null,
        department_name: p.department_name || null,
        department_code: p.department_code || null,
        position_id: p.position_id || null,
        position_name: p.position_name || null,
        similar_term_id: p.similar_term_id || null,
        similar_term_name: p.similar_term_name || null,
        is_similar: !!p.similar_term_id,
        is_primary: p.is_primary,
        public_visible: p.public_visible
      }))
      await talents.updateTalentProfessions(talentId, payload)
      const response = await talents.getTalentByIdWithRelations(talentId)
      if (response?.data) setTalent(response.data)
      setIsEditingProfessions(false)
      toast.success('Professions saved', { duration: 2000, position: 'bottom-right' })
      document.getElementById('professions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (err) {
      toast.error('Failed to save professions', { duration: 2000, position: 'bottom-right' })
    } finally {
      setProfessionSaving(false)
    }
  }

  const toggleProfessionVisibility = async (professionId, currentVisibility) => {
    try {
      const result = await talents.updateTalentProfession(talentId, professionId, { public_visible: !currentVisibility })
      if (result.success) {
        setTalent(prev => ({
          ...prev,
          professions: prev.professions.map(prof =>
            prof.id === professionId ? { ...prof, public_visible: !currentVisibility } : prof
          )
        }))
      }
    } catch (error) {
      console.error('Error toggling profession visibility:', error)
      toast.error('Error updating visibility: ' + (error.response?.data?.message || error.message), { duration: 3000, position: 'bottom-right' })
    }
  }

  return (
    <SectionBlock id="professions">
      <SectionHeader
        icon={Briefcase}
        title="Professions"
        action={!isEditingProfessions && hasPermission('talent-service', `${talent.talent_type}_professions`, 'update') ? (
          <button onClick={openProfessionsEdit} onMouseEnter={loadProfessionOptions} onFocus={loadProfessionOptions} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors">
            <Edit className="h-3.5 w-3.5" /> {talent.professions?.length > 0 ? 'Edit' : 'Add'}
          </button>
        ) : null}
      />

      {isEditingProfessions ? (
        <div className="space-y-4">
          {editingProfessions.map((prof, index) => (
            <div key={index} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">Profession {index + 1}</span>
                {editingProfessions.length > 1 && hasPermission('talent-service', `${talent.talent_type}_professions`, 'update') && (
                  <button onClick={() => setEditingProfessions(prev => prev.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Department</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    value={prof.department_id ? String(prof.department_id) : ''}
                    onChange={val => {
                      const dept = deptOptions.find(d => d.id === Number(val))
                      setEditingProfessions(prev => {
                        const u = [...prev]
                        u[index] = {
                          ...u[index],
                          department_id: val ? Number(val) : null,
                          department_name: dept?.name || '',
                          department_code: dept?.code || null
                        }
                        return u
                      })
                    }}
                    options={deptOptions.map(d => ({ value: String(d.id), label: d.name }))}
                    placeholder={professionOptionsLoading ? 'Loading…' : 'Select department'}
                    disabled={professionOptionsLoading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Profession *</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    value={prof.profession_id ? String(prof.profession_id) : ''}
                    onChange={val => {
                      const selectedProf = professionOptions.find(p => p.id === Number(val))
                      setEditingProfessions(prev => {
                        const u = [...prev]
                        u[index] = { ...u[index], profession_id: val ? Number(val) : null, profession_name: selectedProf?.name || '' }
                        if (selectedProf?.department_id) {
                          const dept = deptOptions.find(d => d.id === selectedProf.department_id)
                          if (dept) {
                            u[index] = {
                              ...u[index],
                              department_id: dept.id,
                              department_name: dept.name,
                              department_code: dept.code || null
                            }
                          }
                        }
                        return u
                      })
                    }}
                    options={professionOptions
                      .filter(p => !prof.department_id || p.department_id === prof.department_id)
                      .map(p => ({ value: String(p.id), label: p.name }))}
                    placeholder={professionOptionsLoading ? 'Loading…' : 'Select profession'}
                    disabled={professionOptionsLoading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Position</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    value={prof.position_id ? String(prof.position_id) : ''}
                    onChange={val => {
                      const pos = positionOptions.find(p => p.id === Number(val))
                      setEditingProfessions(prev => {
                        const u = [...prev]
                        u[index] = { ...u[index], position_id: val ? Number(val) : null, position_name: pos?.name || '' }
                        return u
                      })
                    }}
                    options={positionOptions.map(p => ({ value: String(p.id), label: p.name }))}
                    placeholder={professionOptionsLoading ? 'Loading…' : 'Select position'}
                    disabled={professionOptionsLoading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Similar Profession</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    value={prof.similar_term_id ? String(prof.similar_term_id) : ''}
                    onChange={val => {
                      const term = similarTermOptions.find(t => t.id === Number(val))
                      setEditingProfessions(prev => {
                        const u = [...prev]
                        u[index] = {
                          ...u[index],
                          similar_term_id: val ? Number(val) : null,
                          similar_term_name: term?.value || term?.name || '',
                          is_similar: !!val
                        }
                        // If profession not yet set, auto-populate from similar term's parent profession
                        if (term?.profession_id && !u[index].profession_id) {
                          const parentProf = professionOptions.find(p => p.id === term.profession_id)
                          if (parentProf) {
                            u[index].profession_id = parentProf.id
                            u[index].profession_name = parentProf.name
                            if (parentProf.department_id) {
                              const dept = deptOptions.find(d => d.id === parentProf.department_id)
                              if (dept) {
                                u[index].department_id = dept.id
                                u[index].department_name = dept.name
                                u[index].department_code = dept.code || null
                              }
                            }
                          }
                        }
                        return u
                      })
                    }}
                    options={similarTermOptions
                      .filter(t => !prof.profession_id || t.profession_id === prof.profession_id)
                      .map(t => ({ value: String(t.id), label: t.value || t.name }))}
                    placeholder={professionOptionsLoading ? 'Loading…' : 'Select similar'}
                    disabled={professionOptionsLoading}
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-6">
                  <Checkbox
                    label="Primary"
                    color="primary"
                    size="small"
                    checked={!!prof.is_primary}
                    onChange={(checked) => setEditingProfessions(prev => prev.map((p, i) => ({ ...p, is_primary: checked ? i === index : (i === index ? false : p.is_primary) })))}
                  />
                  <Checkbox
                    label="Public visible"
                    color="primary"
                    size="small"
                    checked={prof.public_visible !== false}
                    onChange={(checked) => setEditingProfessions(prev => { const u = [...prev]; u[index] = { ...u[index], public_visible: checked }; return u })}
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-3">
            {hasPermission('talent-service', `${talent.talent_type}_professions`, 'update') && (
              <button
                onClick={() => setEditingProfessions(prev => [...prev, emptyProfession()])}
                disabled={professionSaving}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50"
              >
                <Plus className="h-3 w-3" /> Add profession
              </button>
            )}
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setIsEditingProfessions(false)} disabled={professionSaving} className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg text-sm text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors disabled:opacity-50">
                <XIcon className="h-4 w-4 inline mr-1.5" />Cancel
              </button>
              <button onClick={handleProfessionsSave} disabled={professionSaving || professionOptionsLoading} className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2">
                {professionSaving ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Saving...</> : <><Save className="h-4 w-4" />Save</>}
              </button>
            </div>
          </div>
        </div>
      ) : talent.professions?.length > 0 ? (
        <div className="space-y-3">
          {talent.professions.map((prof, index) => (
            <div key={index} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Profession</span>
                  <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{getProfessionName(prof) || '—'}</p>
                  {getSimilarTermName(prof) && <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">~ {getSimilarTermName(prof)}</p>}
                </div>
                <div>
                  <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Department</span>
                  <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{getDepartmentName(prof) || '—'}</p>
                </div>
                {getPositionName(prof) && (
                  <div>
                    <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Position</span>
                    <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{getPositionName(prof)}</p>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {prof.is_primary && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />Primary
                  </span>
                )}
                {hasPermission('talent-service', `${talent.talent_type}_professions`, 'update') ? (
                  <button
                    onClick={() => toggleProfessionVisibility(prof.id, prof.public_visible)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md transition-colors cursor-pointer hover:opacity-80 ${prof.public_visible ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-light-bg-secondary dark:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary'}`}
                    title={prof.public_visible ? 'Click to hide from public' : 'Click to show publicly'}
                  >
                    {prof.public_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {prof.public_visible ? 'Public' : 'Hidden'}
                  </button>
                ) : (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md ${prof.public_visible ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-light-bg-secondary dark:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary'}`}>
                    {prof.public_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {prof.public_visible ? 'Public' : 'Hidden'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No professions specified</p>
      )}
    </SectionBlock>
  )
}

export default ProfessionsSection
